import { skipToken } from '@reduxjs/toolkit/query';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { updateProductServiceId } from 'api/treatmentAPI';
import { handleError } from 'services/Error';
import { useGetProductByOidQuery } from 'store/api/referenceApi';

const Title = 'Change NDC';

export const ChangeNdcDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    submitCallback,
}) => {
    const { payerId, treatmentId, productServiceOid, productServiceId } = treatmentDetails ?? {};
    const { data: productData, isLoading: productLoading } = useGetProductByOidQuery(
        productServiceOid ?? skipToken,
    );
    const ndcOptions = (productData?.codes ?? [])
        .map((code) => ({
            value: code.code,
            name: code.code,
        }))
        .filter((option) => option.value !== productServiceId);

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                productServiceId: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await updateProductServiceId({
                        payerId,
                        treatmentId,
                        productServiceId: values.productServiceId,
                    });

                    submitCallback();

                    SuccessToast({ message: 'NDC updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to change NDC. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Change NDC"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <SuggestSingleSelectFormik
                            id="productServiceId"
                            label="NDC"
                            required
                            isLoading={productLoading}
                            values={ndcOptions}
                            onChange={(newValue) =>
                                formik.setFieldValue('productServiceId', newValue?.value)
                            }
                            selectedItem={ndcOptions.find(
                                ({ value }) => value === formik.values.productServiceId,
                            )}
                        />
                    }
                />
            )}
        </Formik>
    );
};

ChangeNdcDialog.Title = Title;

ChangeNdcDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        payerId: PropTypes.string.isRequired,
        treatmentId: PropTypes.string.isRequired,
        productServiceOid: PropTypes.string.isRequired,
        productServiceId: PropTypes.string,
    }),
    submitCallback: PropTypes.func.isRequired,
};
